import React, { useState, useEffect } from 'react';
import useQuranReader from '../hooks/useQuranReader';
import { quranStructure } from '../utils/constants';
import { searchQuran } from '../utils/api';

const SearchModal = ({ isOpen, onClose }) => {
  const { 
    navigateTo, 
    selectedTranslation, 
    availableEditions,
    loadingEditions 
  } = useQuranReader();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSurah, setSelectedSurah] = useState('all');
  const [searchTranslation, setSearchTranslation] = useState(selectedTranslation);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState({ count: 0, matches: [] });
  const [error, setError] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);
  
  // Update search translation whenever the user's selected translation changes
  useEffect(() => {
    setSearchTranslation(selectedTranslation);
  }, [selectedTranslation]);
  
  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setSearchTerm('');
      setResults({ count: 0, matches: [] });
      setError('');
      setSearchPerformed(false);
      // Reset to current selected translation
      setSearchTranslation(selectedTranslation);
    }
  }, [isOpen, selectedTranslation]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchTerm.trim()) return;
    
    setLoading(true);
    setError('');
    setSearchPerformed(true);
    
    try {
      const searchResult = await searchQuran(searchTerm, selectedSurah, searchTranslation);
      setResults(searchResult);
    } catch (err) {
      console.error('Search error:', err);
      setError('An error occurred while searching. Please try again.');
      setResults({ count: 0, matches: [] });
    } finally {
      setLoading(false);
    }
  };

  const goToVerse = (surah, verse) => {
    navigateTo(surah, verse);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-label="Search the Quran">
      <div
        className="rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mt-16 animate-fadeIn"
        style={{ backgroundColor: 'var(--color-card-bg)' }}
      >
        <div className="p-6 border-b" style={{ borderColor: 'var(--color-card-border)' }}>
          <h2 className="text-xl font-bold text-heading mb-4 font-kufi">Search the Quran</h2>
          
          <form onSubmit={handleSearch} className="space-y-4">
            {/* Search term input */}
            <div>
              <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                Search Term
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Enter search term..."
                className="w-full p-3 border border-input-border bg-input-bg text-charcoal rounded-md shadow-sm focus:border-deep-green focus:ring-2 focus:ring-deep-green font-lato"
                required
              />
            </div>
            
            {/* Filter options - on larger screens side by side, stacked on mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Surah selection */}
              <div>
                <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Surah
                </label>
                <select
                  value={selectedSurah}
                  onChange={(e) => setSelectedSurah(e.target.value)}
                  className="w-full p-3 border border-input-border bg-input-bg text-charcoal rounded-md shadow-sm focus:border-deep-green focus:ring-2 focus:ring-deep-green font-lato"
                >
                  <option value="all">All Surahs</option>
                  {quranStructure.map(surah => (
                    <option key={surah.number} value={surah.number}>
                      {surah.number}: {surah.englishName}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Translation selection */}
              <div>
                <label className="block text-sm font-medium mb-1 font-lato" style={{ color: 'var(--color-charcoal)' }}>
                  Translation
                </label>
                <select
                  value={searchTranslation}
                  onChange={(e) => setSearchTranslation(e.target.value)}
                  className="w-full p-3 border border-input-border bg-input-bg text-charcoal rounded-md shadow-sm focus:border-deep-green focus:ring-2 focus:ring-deep-green font-lato"
                  disabled={loadingEditions}
                >
                  {loadingEditions ? (
                    <option>Loading translations...</option>
                  ) : (
                    availableEditions.translations.map(translation => (
                      <option key={translation.identifier} value={translation.identifier}>
                        {translation.language === 'en' ? 'English' : 
                          new Intl.DisplayNames(['en'], { type: 'language' }).of(translation.language)} - {translation.englishName}
                      </option>
                    ))
                  )}
                </select>
              </div>
            </div>
            
            <div className="flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-light-gray text-charcoal rounded-md hover:bg-light-gold transition-colors font-lato mr-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-deep-green text-white rounded-md hover:bg-medium-green transition-colors font-lato"
                disabled={loading}
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
          </form>
        </div>
        
        {/* Search Results */}
        <div className="p-6">
          {loading && (
            <div className="flex justify-center py-8">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-deep-green border-r-transparent"></div>
            </div>
          )}
          
          {error && (
            <div className="text-red-500 mb-4 font-lato">{error}</div>
          )}
          
          {!loading && !error && searchPerformed && (
            <>
              <div className="mb-4">
                <h3 className="text-lg font-semibold font-kufi">
                  {!results.matches || results.matches.length === 0 
                    ? 'No results found' 
                    : `Found ${results.count} result${results.count !== 1 ? 's' : ''}`}
                </h3>
                {results.matches && results.matches.length > 0 && (
                  <p className="text-sm font-lato mt-1" style={{ color: 'var(--color-charcoal)', opacity: 0.7 }}>
                    Searched in: {results.matches[0].edition.englishName} ({results.matches[0].edition.language})
                  </p>
                )}
              </div>
              
              <div className="space-y-4">
                {results.matches && results.matches.map((result, index) => (
                  <div 
                    key={index} 
                    className="p-4 border border-card-border rounded-lg hover:bg-light-gray transition-colors"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium text-heading font-kufi">
                        {result.surahEnglishName} ({result.surahName}) - Verse {result.numberInSurah}
                      </div>
                      <button
                        onClick={() => goToVerse(result.surahNumber, result.numberInSurah)}
                        className="text-xs px-2 py-1 bg-light-gold text-deep-green rounded hover:bg-gold transition-colors font-lato"
                      >
                        Go to verse
                      </button>
                    </div>
                    <p className="mb-2 font-lato" style={{ color: 'var(--color-charcoal)' }}>{result.text}</p>
                    
                    {/* We'll provide an option to load Arabic text on demand */}
                    <div className="mt-3 flex justify-end">

                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchModal;